import React, { useEffect, useState } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';
import DocumentsPage from './DocumentsPage';
import CarrierTasksPage from './CarrierTasksPage';
import DashboardOverview from '../components/dashboard/DashboardOverview';

const CarrierDashboardPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [onboardingComplete, setOnboardingComplete] = useState(true); // default true to prevent flash
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [carrierNotifs, setCarrierNotifs] = useState<any[]>([]);
  const [showCarrierNotifs, setShowCarrierNotifs] = useState(false);
  const [carrierUnread, setCarrierUnread] = useState(0);
  const [carrierProfile, setCarrierProfile] = useState<any>(null);

  useEffect(() => {
    // Check onboarding status
    if (typeof window !== 'undefined') {
      const done = localStorage.getItem('loaddrive_onboarding_complete') === 'true';
      setOnboardingComplete(done);
      setOnboardingChecked(true);
    }
    fetchPendingTasks();
    fetchCarrierNotifications();
    // Fetch carrier profile name
    const fetchProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        const { data } = await supabase.from('profiles').select('first_name, last_name').eq('id', session.user.id).single();
        if (data) setCarrierProfile(data);
      }
    };
    fetchProfile();
  }, [location]);

  const fetchCarrierNotifications = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const { data } = await supabase
        .from('notifications')
        .select('*')
        .or(`recipient_id.eq.${session.user.id},type.eq.broker_message`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (data) {
        setCarrierNotifs(data);
        setCarrierUnread(data.filter((n: any) => !n.read).length);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const fetchPendingTasks = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const { data: apps } = await supabase
        .from('applications')
        .select('company_code')
        .eq('user_id', session.user.id);

      if (!apps || apps.length === 0) return;

      const companyCodes = apps.map(a => a.company_code);

      const { data: agreements } = await supabase
        .from('agreements')
        .select('id, status, carrier_id')
        .in('company_code', companyCodes)
        .eq('status', 'active')
        .or(`carrier_id.eq.${session.user.id},carrier_id.is.null`);

      setPendingTasks(agreements?.length || 0);
    } catch (error) {
      console.error('Error fetching pending tasks:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium ${
      isActive
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
    }`;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-screen-2xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
             <img src="/images/loaddrive-logo.png" alt="LoadDrive" className="h-8" />
              <span className="text-sm text-gray-500">Carrier Portal</span>
            </div>

            <nav className="flex gap-2">
              <NavLink to="/carrier/dashboard" end className={navLinkClass}>
                Dashboard
              </NavLink>

              {onboardingChecked && !onboardingComplete && (
                <NavLink to="/carrier/onboarding" className={navLinkClass}>
                  Onboarding
                </NavLink>
              )}

              <NavLink to="/carrier/documents" className={navLinkClass}>
                Documents
              </NavLink>

              <NavLink
                to="/carrier/tasks"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }`
                }
              >
                Rate Cons & Agreements
                {pendingTasks > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {pendingTasks}
                  </span>
                )}
              </NavLink>
            </nav>

            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  onClick={() => setShowCarrierNotifs(!showCarrierNotifs)}
                  className="relative p-2 text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>

                  {carrierUnread > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {carrierUnread}
                    </span>
                  )}
                </button>

                {showCarrierNotifs && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50 max-h-96 overflow-y-auto">
                    <div className="p-3 border-b flex justify-between items-center">
                      <h3 className="font-semibold text-sm">Notifications</h3>
                      {carrierUnread > 0 && (
                        <button
                          onClick={async () => {
                            const ids = carrierNotifs.filter(n => !n.read).map(n => n.id);
                            if (ids.length > 0) {
                              await supabase
                                .from('notifications')
                                .update({ read: true })
                                .in('id', ids);
                            }
                            setCarrierNotifs(prev => prev.map(n => ({ ...n, read: true })));
                            setCarrierUnread(0);
                          }}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>

                    {carrierNotifs.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">
                        No notifications
                      </div>
                    ) : (
                      carrierNotifs.map(n => (
                        <div
                          key={n.id}
                          className={`p-3 text-sm cursor-pointer border-l-4 ${
                            !n.read ? 'bg-blue-50 border-l-blue-500' : 'bg-white border-l-transparent hover:bg-gray-50'
                          }`}
                          onClick={async () => {
                            if (!n.read) {
                              await supabase
                                .from('notifications')
                                .update({ read: true })
                                .eq('id', n.id);

                              setCarrierNotifs(prev =>
                                prev.map(notif =>
                                  notif.id === n.id ? { ...notif, read: true } : notif
                                )
                              );

                              setCarrierUnread(prev => Math.max(0, prev - 1));
                            }
                            setShowCarrierNotifs(false);

                            // Navigate based on type
                            if (n.type === 'rate_con_assigned') {
                              navigate('/carrier/tasks');
                            } else if (n.type === 'doc_approved' || n.type === 'doc_rejected') {
                              navigate('/carrier/documents');
                            }
                          }}
                        >
                          <p className={`${!n.read ? 'font-bold text-gray-900' : 'font-normal text-gray-500'}`}>{n.title}</p>
                          <p className="text-gray-600 mt-1">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(n.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

            {carrierProfile && (
                <span className="text-sm text-gray-700">
                  {carrierProfile.first_name} {carrierProfile.last_name}
                </span>
              )}
              <button
                type="button"
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-800 text-sm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto py-6 px-4">
        <Routes>
          <Route path="/" element={<DashboardOverview />} />
          <Route path="dashboard" element={<DashboardOverview />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="tasks" element={<CarrierTasksPage />} />
        </Routes>
      </div>
    </div>
  );
};

export default CarrierDashboardPage;